import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { StaffSideMenu } from "../../staff/StaffSideMenu";
import API_URL from "../../../api/API";
import { useStaffAuthContext } from "../../staff/useStaffAuthContext";

export const PhaseEdit = () => {
  const { staff } = useStaffAuthContext();
  const { id } = useParams(); // Get the :id from the URL
  const navigate = useNavigate(); // For navigation after editing
  const [title, setTitle] = useState(""); // State to store the title
  const [isLoading, setIsLoading] = useState(false); // State to manage loading
  const [error, setError] = useState(""); // State to store error messages
  const [successMessage, setSuccessMessage] = useState(""); // State to store success message

  // Fetch the phase data by ID
  useEffect(() => {
    const fetchPhase = async () => {
      try {
        setIsLoading(true);
        setError("");
        const response = await fetch(`${API_URL.GET_PHASE_BY_ID}/${id}`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${staff.token}`,
          },
        });
        const data = await response.json();

        if (!response.ok) {
          setError(data.error || "Failed to load phase data");
        }

        setTitle(data.title);
        setIsLoading(false);
      } catch (err) {
        setError(err.message);
        setIsLoading(false);
      }
    };

    fetchPhase();
  }, [id, staff.token]);

  // Handle form submission to edit the phase
  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      setIsLoading(true);
      setError("");
      setSuccessMessage("");

      const response = await fetch(`${API_URL.PATCH_PHASE_BY_ID}${id}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${staff.token}`,
        },
        body: JSON.stringify({ title }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Failed to update phase");
      }

      setSuccessMessage("Phase updated successfully!");
     
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex h-screen">
      <StaffSideMenu />

      {/* Main Content */}
      <div className="flex-1 p-6 bg-gray-100 pt-16">
        {/* Go Back Button */}
        <div className="flex justify-end mb-4">
          <button
            onClick={() => navigate(-1)} // Go back to the previous page
            className="px-4 py-2 bg-red-500 text-white font-semibold rounded-md hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
          >
            Go Back
          </button>
        </div>

        <h1 className="text-2xl font-bold mb-6">Edit Phase</h1>

        <form
          onSubmit={handleSubmit}
          className="bg-white shadow-md rounded px-8 pt-6 pb-8 mb-4 max-w-lg mx-auto"
        >
          {/* Title Input */}
          <div className="mb-4">
            <label
              htmlFor="title"
              className="block text-gray-700 text-sm font-bold mb-2"
            >
              Phase Title
            </label>
            <input
              type="text"
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Enter phase title"
              className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
              required
              disabled={isLoading}
            />
          </div>

          {/* Error Message */}
          {error && <p className="text-red-500 text-sm mb-4">{error}</p>}

          {/* Success Message */}
          {successMessage && (
            <p className="text-green-500 text-sm mb-4">{successMessage}</p>
          )}

          {/* Submit Button */}
          <div className="flex items-center justify-between">
            <button
              type="submit"
              disabled={isLoading}
              className={`${
                isLoading ? "bg-blue-300" : "bg-blue-500 hover:bg-blue-700"
              } text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline`}
            >
              {isLoading ? "Editing..." : "Edit Phase"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
